import { getPmStatus } from "../utils/pm";
import { hasCapability, isMine } from "../utils/capabilities";
import { computeLegacyJobTotal } from "../utils/billTotals";

export function computeDashboardData(props) {
  const {
    customers = [],
    jobsRaw = [],
    serviceTicketsRaw = [],
    quotesRaw = [],
    purchaseOrdersRaw = [],
    contractsRaw = [],
    vendorsRaw = [],
    notificationsRaw = [],
    dismissedNotificationIds = [],
    timeEntriesRaw = [],
    orgId,
    uid,
    user,
    role,
    capabilities,
  } = props;

  const canSeeAllQuotes = hasCapability(role, capabilities, "viewAllQuotes");
  const canSeeAllPOs = hasCapability(role, capabilities, "viewAllPOs");
  const canSeeAllTickets = hasCapability(
    role,
    capabilities,
    "viewAllServiceTickets"
  );
  const canSeeRecentActivity = hasCapability(
    role,
    capabilities,
    "viewRecentActivity"
  );
  const canCreateQuotes = hasCapability(role, capabilities, "createQuotes");

  const scopedQuotes = canSeeAllQuotes
    ? quotesRaw
    : quotesRaw.filter((q) => isMine(q, user));
  const scopedPOs = canSeeAllPOs
    ? purchaseOrdersRaw
    : purchaseOrdersRaw.filter((p) => isMine(p, user));
  const scopedTickets = canSeeAllTickets
    ? serviceTicketsRaw
    : serviceTicketsRaw.filter((t) => isMine(t, user));

  const totalCustomers = customers.length;

  const activeJobCount = jobsRaw.filter((j) => {
    const s = j.status || "unassigned";
    // Maintenance visit jobs spawned by a contract sit at status
    // "scheduled" / "skipped" until admin activates them. Neither is
    // "open / active" from the dashboard's perspective.
    if (s === "scheduled" || s === "skipped") return false;
    return s !== "closed" && s !== "canceled";
  }).length;

  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

  // Recent-Activity tile count = UNACKNOWLEDGED service records from
  // the last 7 days. Matches the Service Tickets tile semantic next to
  // it (count = unacknowledged) so clicking "Acknowledge" on a record
  // actually drops the badge. Older un-acked records (>7 days) silently
  // fall off so the counter doesn't pile up forever.
  const recentRecordCount = customers
    .flatMap((customer) =>
      (customer.locations || []).flatMap((location) =>
        (location.equipment || []).flatMap((equipment) =>
          (equipment.serviceRecords || []).filter(
            (record) =>
              !record.deleted &&
              !record.acknowledged &&
              new Date(record.createdAt || record.date).getTime() >=
                sevenDaysAgo
          )
        )
      )
    ).length;

  const allEquipment = customers.flatMap((customer) =>
    (customer.locations || []).flatMap((location) =>
      (location.equipment || []).map((equipment) => ({
        ...equipment,
        customerId: customer.id,
        customerName: customer.name,
        locationId: location.id,
        locationName: location.name,
      }))
    )
  );

  const overduePmCount = allEquipment.filter((equipment) => {
    const status = getPmStatus(equipment);
    return status?.overdue;
  }).length;

  const dueSoonPmCount = allEquipment.filter((equipment) => {
    const status = getPmStatus(equipment);
    return status?.dueSoon;
  }).length;

  const optedInEquipment = customers.flatMap((customer) =>
    (customer.locations || [])
      .filter((loc) => loc.notifyOnEquipmentStatusFor?.[orgId] === true)
      .flatMap((loc) => loc.equipment || [])
  );
  const greenCount = optedInEquipment.filter(
    (eq) => (eq.status || "green") === "green"
  ).length;
  const yellowCount = optedInEquipment.filter(
    (eq) => eq.status === "yellow"
  ).length;
  const redCount = optedInEquipment.filter((eq) => eq.status === "red").length;
  const hasAnyOptIn = optedInEquipment.length > 0;

  // Messages: unread + not dismissed + targets me (or untargeted).
  const dismissedSet = new Set(dismissedNotificationIds);
  const unreadMessageCount = notificationsRaw.filter((n) => {
    if (n.type !== "new_message") return false;
    if (n.read) return false;
    if (dismissedSet.has(n.id)) return false;
    if (Array.isArray(n.targetUids) && n.targetUids.length > 0) {
      if (!uid || !n.targetUids.includes(uid)) return false;
    }
    return true;
  }).length;

  // Invoices count: each entry in bills[] counts; legacy single-finalize
  // jobs count as 1. Once mark-paid lands, swap to "Unpaid".
  const issuedInvoiceCount = jobsRaw.reduce((s, j) => {
    if ((j.bills || []).length > 0) return s + j.bills.length;
    if (j.billFinalized) return s + 1;
    return s;
  }, 0);

  // Top-N lists for the quadrant mini-views. Computed once here so each
  // QuadrantView can read pre-sorted slices without re-walking the data.
  const customerById = Object.fromEntries(customers.map((c) => [c.id, c]));
  const vendorById = Object.fromEntries(vendorsRaw.map((v) => [v.id, v]));

  // Active jobs sorted by status priority (in_progress > accepted >
  // unassigned > others) then most recent.
  const activeJobs = jobsRaw.filter((j) => {
    const s = j.status || "unassigned";
    if (s === "scheduled" || s === "skipped") return false;
    return s !== "closed" && s !== "canceled";
  });
  const STATUS_PRIORITY = {
    in_progress: 0,
    accepted: 1,
    unassigned: 2,
  };
  const activeJobsSorted = [...activeJobs].sort((a, b) => {
    const sa = STATUS_PRIORITY[a.status] ?? 9;
    const sb = STATUS_PRIORITY[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });

  // Unacked tickets, newest first.
  const unackedTickets = scopedTickets
    .filter((t) => !t.acknowledged)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // Open quotes (not approved), newest first.
  const openQuotes = scopedQuotes
    .filter((q) => q.status !== "approved")
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // Recent invoices: flatten jobs->bills with creation date, newest first.
  // Legacy single-finalize jobs count as one synthetic row. Bill totals
  // are stored on `grandTotal` (see generateBill in useOrgData) -- earlier
  // versions of this helper read `b.total` which doesn't exist on bill
  // entries, so the Invoices mini-view showed $0 across the board.
  // Fallback chain covers any shape variant.
  const billTotalOf = (b) =>
    Number(b?.grandTotal ?? b?.total ?? b?.subtotal ?? 0) || 0;
  const recentInvoices = [];
  jobsRaw.forEach((j) => {
    if ((j.bills || []).length > 0) {
      j.bills.forEach((b) => {
        recentInvoices.push({
          job: j,
          bill: b,
          createdAt: b.finalizedAt || b.createdAt || j.createdAt,
          total: billTotalOf(b),
          invoiceNumber: b.invoiceNumber || "",
        });
      });
    } else if (j.billFinalized) {
      // Legacy single-finalize jobs: total is NOT stored as a field --
      // it's computed from costing line items. Use the shared
      // computeLegacyJobTotal so this matches InvoicesPage exactly.
      const cust = customerById[j.customerId];
      const { grandTotal } = computeLegacyJobTotal(j, cust);
      recentInvoices.push({
        job: j,
        bill: null,
        createdAt: j.billFinalizedAt || j.createdAt,
        total: grandTotal,
        invoiceNumber: j.invoiceNumber || "",
      });
    }
  });
  recentInvoices.sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  // Open POs, newest first.
  const openPOs = scopedPOs
    .filter((p) => p.status !== "received")
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // Closed jobs awaiting bill: status === "closed" && !billFinalized.
  // The billing dept's actionable queue. Oldest closedAt first so the
  // mini-view shows the most stale (most urgent) jobs at the top.
  const closedJobsAwaitingBill = jobsRaw
    .filter((j) => j.status === "closed" && !j.billFinalized)
    .sort(
      (a, b) =>
        new Date(a.closedAt || a.updatedAt || 0) -
        new Date(b.closedAt || b.updatedAt || 0)
    );

  // Active contracts, alphabetical by customer.
  const activeContracts = contractsRaw
    .filter((c) => c.status === "active")
    .sort((a, b) => {
      const an = customerById[a.customerId]?.name || "";
      const bn = customerById[b.customerId]?.name || "";
      return an.localeCompare(bn);
    });

  // Active vendors, alphabetical.
  const activeVendors = vendorsRaw
    .filter((v) => v.status === "active")
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  // PM lists with diff-day numbers for sort. Equipment-side carries the
  // customer/location names already injected by allEquipment above.
  const pmDueSoonList = allEquipment
    .map((e) => ({ eq: e, pm: getPmStatus(e) }))
    .filter(({ pm }) => pm?.dueSoon)
    .sort((a, b) => a.pm.diffDays - b.pm.diffDays);
  const pmOverdueList = allEquipment
    .map((e) => ({ eq: e, pm: getPmStatus(e) }))
    .filter(({ pm }) => pm?.overdue)
    .sort((a, b) => a.pm.diffDays - b.pm.diffDays);

  // Recent service records, newest first, materialized with parent
  // pointers so each row can show equipment + customer.
  const recentRecords = [];
  customers.forEach((customer) => {
    (customer.locations || []).forEach((location) => {
      (location.equipment || []).forEach((equipment) => {
        (equipment.serviceRecords || []).forEach((record) => {
          if (record.deleted) return;
          const t = new Date(record.createdAt || record.date || 0).getTime();
          if (t < sevenDaysAgo) return;
          recentRecords.push({
            record,
            customer,
            location,
            equipment,
            ts: t,
          });
        });
      });
    });
  });
  recentRecords.sort((a, b) => b.ts - a.ts);

  // Unread message notifications, newest first.
  const unreadMessages = notificationsRaw
    .filter((n) => {
      if (n.type !== "new_message") return false;
      if (n.read) return false;
      if (dismissedSet.has(n.id)) return false;
      if (Array.isArray(n.targetUids) && n.targetUids.length > 0) {
        if (!uid || !n.targetUids.includes(uid)) return false;
      }
      return true;
    })
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  // MyTime: this user's straight/OT/DT minutes over the past 7 days.
  // Rolling window for now; switches to org payPeriodConfig when
  // [[project-payroll-redesign]] ships. Visibility (further down)
  // also uses the all-time count so users who have never clocked
  // don't see a permanent "0:00" tile.
  const myEntriesAll = timeEntriesRaw.filter((e) => e.userId === uid);
  const sevenDaysAgoMs = sevenDaysAgo;
  const myEntriesThisWeek = myEntriesAll.filter((e) => {
    const t = new Date(e.endAt || e.startAt || e.createdAt).getTime();
    return t >= sevenDaysAgoMs;
  });
  const sumMinutes = (type) =>
    myEntriesThisWeek
      .filter((e) => e.payType === type)
      .reduce((s, e) => s + (e.durationMinutes || 0), 0);
  const myTimeStraight = sumMinutes("straight");
  const myTimeOvertime = sumMinutes("overtime");
  const myTimeDouble = sumMinutes("double");
  const myHasAnyTimeEver = myEntriesAll.length > 0;

  return {
    // Raw passthrough for tiles that need full lists
    customers,
    jobsRaw,
    contractsRaw,
    vendorsRaw,
    scopedQuotes,
    scopedPOs,
    scopedTickets,

    // Cached counts
    totalCustomers,
    activeJobCount,
    recentRecordCount,
    overduePmCount,
    dueSoonPmCount,
    unreadMessageCount,
    issuedInvoiceCount,

    // MyTime tile
    myTimeStraight,
    myTimeOvertime,
    myTimeDouble,
    myHasAnyTimeEver,

    // Quadrant mini-view top-N lists + lookup maps
    customerById,
    vendorById,
    activeJobsSorted,
    unackedTickets,
    openQuotes,
    recentInvoices,
    closedJobsAwaitingBill,
    openPOs,
    activeContracts,
    activeVendors,
    pmDueSoonList,
    pmOverdueList,
    recentRecords,
    unreadMessages,

    // Equipment Health strip data
    optedInEquipment,
    greenCount,
    yellowCount,
    redCount,
    hasAnyOptIn,

    // Capability flags
    canSeeRecentActivity,
    canCreateQuotes,
  };
}
